import { useEffect, useMemo, useState } from "react";
import { Search, Check, RefreshCw, Target } from "lucide-react";
import { T } from "../../ui/tokens";
import { fmt, fmtDate } from "../../lib/format";
import { useFaturasPendentes } from "./useFaturasPendentes";
// Mesmas heurísticas usadas pelo painel guiado (PainelConciliacao) — viviam
// aqui até serem precisas nos dois sítios; ver matching.ts.
import { scoreProximidade, dentroDoIntervaloPlausivel, dataImpossivel } from "./matching";
import type { FaturaLigada, FaturaPendente, MovimentoExtrato } from "./types";

function BarraProgresso({ busy }: { busy: boolean }) {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (!busy) {
      if (p > 0) { setP(100); const t = setTimeout(() => setP(0), 500); return () => clearTimeout(t); }
      return;
    }
    setP(8);
    const t = setInterval(() => setP((v) => (v < 90 ? v + (90 - v) * 0.12 : v)), 180);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);
  if (p <= 0) return null;
  return (
    <div style={{ height: 3, background: T.border, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${p}%`, background: T.blue, transition: "width .18s linear" }} />
    </div>
  );
}

interface Props {
  movimento: MovimentoExtrato;
  empresa: string;
  busy: boolean;
  erro?: string | null;
  onAssociar: (faturaIds: number[]) => void;
  onClose: () => void;
}

// Modal para associar (ou corrigir) manualmente um movimento bancário a uma
// ou mais faturas — usado quando não há sugestão automática, ou quando uma
// associação existente (pendente ou já confirmada) está errada. Suporta
// selecionar várias faturas para o mesmo movimento ("outro conjunto de
// pagamentos" — um único pagamento pode cobrir mais do que uma fatura).
export default function ModalCorrigirFatura({ movimento: m, empresa, busy, erro, onAssociar, onClose }: Props) {
  const tipo = m.tipo === "debito" ? "fornecedor" : "cliente";
  const { data, isLoading } = useFaturasPendentes(empresa, tipo);
  const [filtro, setFiltro] = useState("");
  const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());

  const listaCompleta = useMemo(() => data?.faturas || [], [data]);

  // Sem pesquisa: só mostra candidatos plausíveis (mesmo mês/±1 mês, valor
  // próximo — ver dentroDoIntervaloPlausivel) ordenados por proximidade. Ao
  // escrever na barra, o filtro plausível é ignorado e pesquisa-se por
  // fornecedor/cliente, número do documento OU valor em TODAS as faturas
  // pendentes — o utilizador pode estar a corrigir precisamente um caso fora
  // do intervalo habitual, e não pode ficar sem forma de o encontrar.
  //
  // Uma fatura já selecionada NUNCA desaparece da lista, mesmo que deixe de
  // corresponder ao filtro atual (ex: escolhida via pesquisa, depois a
  // pesquisa é limpa) — escondê-la fazia a soma parecer "0,00€ / errada" e o
  // botão de avançar ficava bloqueado sem se perceber porquê (bug real
  // reportado: 3 faturas "selecionadas" mas soma 0,00€, porque a soma só
  // olhava para a lista filtrada, não para a seleção real).
  const candidatos = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    const valorFiltro = parseFloat(filtro.trim().replace(",", "."));
    const temValorFiltro = filtro.trim() !== "" && !Number.isNaN(valorFiltro);

    // Uma fatura datada depois do movimento é fisicamente impossível --
    // nunca aparece, nem por pesquisa explícita (regra absoluta, pedido
    // direto do dono do projeto). Ver dataImpossivel em matching.ts.
    const semImpossiveis = listaCompleta.filter((c: FaturaPendente) => !dataImpossivel(c, m));
    const base = !f
      ? semImpossiveis.filter((c: FaturaPendente) => dentroDoIntervaloPlausivel(c, m))
      : semImpossiveis.filter((c: FaturaPendente) =>
          (c.nome_emitente || "").toLowerCase().includes(f) ||
          (c.numero_documento || "").toLowerCase().includes(f) ||
          (temValorFiltro && Math.abs(Math.abs(c.valor_total || 0) - Math.abs(valorFiltro)) < 0.01)
        );
    const idsBase = new Set(base.map((c: FaturaPendente) => c.id));
    const selecionadasForaDoFiltro = semImpossiveis.filter((c: FaturaPendente) => selecionadas.has(c.id) && !idsBase.has(c.id));
    return [...selecionadasForaDoFiltro, ...base].sort((a: FaturaPendente, b: FaturaPendente) => scoreProximidade(b, m) - scoreProximidade(a, m));
  }, [listaCompleta, filtro, m, selecionadas]);

  const totalPendentes = listaCompleta.length;
  const escondidasPeloFiltroPlausivel = !filtro.trim() && totalPendentes > candidatos.length;

  const toggle = (id: number) => {
    setSelecionadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Calculada sobre a lista COMPLETA (não a filtrada) — nunca depende do que
  // está visível neste momento, só do que está realmente selecionado.
  const somaSelecionadas = listaCompleta
    .filter((c: FaturaPendente) => selecionadas.has(c.id))
    .reduce((acc: number, c: FaturaPendente) => acc + (c.valor_total || 0), 0);
  const valorMov = Math.abs(m.valor);
  const divergeSoma = selecionadas.size > 0 && Math.abs(somaSelecionadas - valorMov) > 0.02;
  // O valor tem de bater sempre certo -- uma só fatura com valor diferente
  // do movimento bloqueia tal como uma soma de várias que não fecha (pedido
  // direto do dono do projeto). Selecionar mais do que uma continua sempre
  // manual -- nunca se soma sozinho.
  const bloqueadoPorSoma = divergeSoma;

  const temMatchAtual = m.match_id != null;
  const faturasAtuais: FaturaLigada[] = m.faturas?.length
    ? m.faturas
    : temMatchAtual && m.fatura_id != null
      ? [{
          fatura_id: m.fatura_id,
          numero_documento: m.numero_documento || null,
          nome_emitente: m.nome_emitente || null,
          nif_emitente: m.nif_emitente || null,
          valor: m.fatura_valor ?? null,
          data_documento: m.fatura_data || null,
          drive_link: m.drive_link || null,
          match_id: m.match_id as number,
          match_estado: m.match_estado === "pendente" ? "pendente" : "confirmado",
        }]
      : [];

  return (
    <div onClick={busy ? undefined : onClose} style={{ position: "fixed", inset: 0, zIndex: 3700, background: "rgba(15,23,42,.65)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface, borderRadius: 16, width: "min(94vw, 620px)", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,.25)" }}>
        <BarraProgresso busy={busy} />
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{temMatchAtual ? "Corrigir associação" : "Associar a uma fatura"}</div>
            <div style={{ fontSize: 12, color: T.textMid, marginTop: 2 }}>
              {fmtDate(m.data_mov)} · {m.descricao} · <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: T.text }}>{fmt(valorMov)}</span>
            </div>
          </div>
          <button disabled={busy} onClick={onClose} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: T.bg, cursor: busy ? "default" : "pointer", fontSize: 14, color: T.textMid, opacity: busy ? .5 : 1 }}>✕</button>
        </div>

        {temMatchAtual && (
          <div style={{ margin: "14px 22px 0", flexShrink: 0 }}>
            <div style={{ padding: "11px 14px", borderRadius: 9, background: T.blueBg, border: `1px solid ${T.blueBorder}` }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.blue, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 7 }}>
                Associação atual
              </div>
              {faturasAtuais.length ? faturasAtuais.map((fatura, index) => (
                <div key={`${fatura.match_id}-${fatura.fatura_id}`} style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: index ? 8 : 0, marginTop: index ? 8 : 0, borderTop: index ? `1px solid ${T.blueBorder}` : "none" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 750, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fatura.nome_emitente || "Entidade não identificada"}
                    </div>
                    <div style={{ fontSize: 11.5, color: T.textMid, marginTop: 2 }}>
                      {fatura.numero_documento || "Sem número"} · {fmtDate(fatura.data_documento || undefined)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 750, color: T.text, fontVariantNumeric: "tabular-nums" }}>{fmt(fatura.valor ?? undefined)}</div>
                    <div style={{ fontSize: 10, color: fatura.match_estado === "confirmado" ? T.green : T.amber, marginTop: 2 }}>
                      {fatura.match_estado === "confirmado" ? "Confirmada" : "Por confirmar"}
                    </div>
                  </div>
                </div>
              )) : (
                <div style={{ fontSize: 12, color: T.textMid }}>A associação existe, mas os detalhes da fatura não estão disponíveis.</div>
              )}
            </div>
            <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8, fontSize: 12, background: T.amberBg, color: T.amber, border: `1px solid ${T.amberBorder}` }}>
              <RefreshCw size={11} strokeWidth={2.5} style={{ verticalAlign: -1, marginRight: 5 }} />
              Ao associar outra fatura, esta ligação será substituída.
            </div>
          </div>
        )}

        <div style={{ padding: "14px 22px 0", flexShrink: 0 }}>
          <div style={{ position: "relative" }}>
            <Search size={14} color={T.textLight} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
            <input
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Procurar por fornecedor/cliente, número do documento ou valor…"
              style={{ width: "100%", padding: "8px 10px 8px 30px", borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13, color: T.text, background: T.bg, outline: "none", boxSizing: "border-box" }}
            />
          </div>
        </div>

        <div style={{ padding: "10px 22px", overflowY: "auto", flex: 1, minHeight: 120 }}>
          {isLoading ? (
            <div style={{ padding: "24px 0", textAlign: "center", color: T.textLight, fontSize: 13 }}>A carregar faturas…</div>
          ) : candidatos.length === 0 ? (
            <div style={{ padding: "24px 0", textAlign: "center", color: T.textLight, fontSize: 13 }}>
              {filtro
                ? "Nenhuma fatura corresponde a esta pesquisa."
                : totalPendentes > 0
                ? `Nenhuma fatura com data/valor próximos deste movimento (de ${totalPendentes} pendente(s)). Pesquisa por nome, número ou valor para ver todas.`
                : `Sem faturas de ${tipo === "fornecedor" ? "fornecedor" : "cliente"} pendentes.`}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {escondidasPeloFiltroPlausivel && (
                <div style={{ fontSize: 11, color: T.textLight, padding: "0 2px 2px" }}>
                  A mostrar só faturas com data/valor próximos ({candidatos.length} de {totalPendentes}). Pesquisa por nome, número ou valor para ver todas.
                </div>
              )}
              {candidatos.map((c, i) => {
                const sel = selecionadas.has(c.id);
                const melhor = i === 0 && !filtro && scoreProximidade(c, m) >= 60;
                const foraDoPlausivel = sel && !dentroDoIntervaloPlausivel(c, m);
                return (
                  <label
                    key={c.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 8,
                      border: `1px solid ${sel ? T.blueBorder : melhor ? T.greenBorder : T.border}`, background: sel ? T.blueBg : T.surface,
                      cursor: "pointer",
                    }}
                  >
                    <input type="checkbox" checked={sel} onChange={() => toggle(c.id)} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.nome_emitente || "—"}</div>
                        {melhor && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9.5, fontWeight: 700, color: T.green, background: T.greenBg, border: `1px solid ${T.greenBorder}`, borderRadius: 20, padding: "1px 6px", flexShrink: 0 }}>
                            <Target size={9} strokeWidth={3} /> mais provável
                          </span>
                        )}
                        {foraDoPlausivel && (
                          <span style={{ fontSize: 9.5, fontWeight: 700, color: T.textMid, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 20, padding: "1px 6px", flexShrink: 0 }}>
                            fora do intervalo habitual
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11.5, color: T.textMid, marginTop: 1 }}>{c.numero_documento} · {fmtDate(c.data_documento)}</div>
                    </div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{fmt(c.valor_total)}</div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {selecionadas.size > 0 && (
          <div style={{ margin: "0 22px", padding: "9px 14px", borderRadius: 8, fontSize: 12, background: divergeSoma ? T.redBg : T.greenBg, color: divergeSoma ? T.red : T.green, border: `1px solid ${divergeSoma ? T.redBorder : T.greenBorder}`, flexShrink: 0 }}>
            {selecionadas.size} fatura(s) selecionada(s) · soma {fmt(somaSelecionadas)}
            {divergeSoma
              ? ` — difere ${fmt(Math.abs(somaSelecionadas - valorMov))} do valor do movimento`
              : selecionadas.size > 1 && " — soma exata, vai criar um batch de pagamento já confirmado como pago"}
          </div>
        )}
        {bloqueadoPorSoma && (
          <div style={{ margin: "6px 22px 0", fontSize: 11.5, color: T.textMid, flexShrink: 0 }}>
            O valor tem de bater exatamente certo com o movimento — escolhe outra fatura, ajusta a seleção, ou junta mais do que uma até a soma fechar.
          </div>
        )}

        {erro && (
          <div style={{ margin: "10px 22px 0", padding: "9px 14px", borderRadius: 8, fontSize: 12.5, background: T.redBg, color: T.red, border: `1px solid ${T.redBorder}`, flexShrink: 0 }}>
            ✗ Não foi possível concluir: {erro}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "14px 22px", borderTop: `1px solid ${T.border}`, background: T.bg, flexShrink: 0 }}>
          <button
            disabled={busy}
            onClick={onClose}
            style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.textMid, fontSize: 12.5, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? .5 : 1 }}
          >
            Cancelar
          </button>
          <button
            disabled={busy || selecionadas.size === 0 || bloqueadoPorSoma}
            onClick={() => onAssociar(Array.from(selecionadas))}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: 8, border: "none", background: T.green, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: busy || selecionadas.size === 0 || bloqueadoPorSoma ? "default" : "pointer", opacity: busy || selecionadas.size === 0 || bloqueadoPorSoma ? .5 : 1 }}
          >
            <Check size={14} strokeWidth={2.5} />
            {selecionadas.size > 1 ? `Criar batch e associar (${selecionadas.size})` : "Associar"}
          </button>
        </div>
      </div>
    </div>
  );
}
